import React from "react";
import { Modal, ModalBody, Spinner } from "reactstrap";

type ConfirmationVariant = 'delete' | 'cancel' | 'warning' | 'info';

interface DeleteModalProps {
  show: boolean;
  onDeleteClick: () => void;
  onCloseClick: () => void;
  title?: string;                    // "Delete Website", "Remove Product"
  message?: string;                  // Custom confirmation message  
  recordName?: string;               // What's being deleted
  recordId?: string;                 // ID/identifier
  deleteButtonText?: string;         // "Delete", "Remove", "Archive"
  isLoading?: boolean;              // Show spinner during deletion
  variant?: ConfirmationVariant;     // Visual variant for different types
  cancelButtonText?: string;         // Custom text for cancel button
}

const DeleteModal: React.FC<DeleteModalProps> = ({ 
  show, 
  onDeleteClick, 
  onCloseClick, 
  title = "Are you sure?",
  message,
  recordName,
  recordId,
  deleteButtonText = "Yes, Delete It!",
  isLoading = false,
  variant = 'delete',
  cancelButtonText = "Close"
}) => {
  // Variant configurations
  const variantConfig = {
    delete: {
      icon: 'ri-delete-bin-line',
      iconColor: 'text-danger',
      confirmButtonColor: 'btn-danger',
      defaultTitle: 'Are you sure?',
      defaultDeleteButtonText: 'Yes, Delete It!'
    },
    cancel: {
      icon: 'ri-error-warning-line',
      iconColor: 'text-warning',
      confirmButtonColor: 'btn-warning',
      defaultTitle: 'Unsaved Changes',
      defaultDeleteButtonText: 'Yes, Cancel'
    },
    warning: {
      icon: 'ri-alert-line',
      iconColor: 'text-warning',
      confirmButtonColor: 'btn-warning',
      defaultTitle: 'Warning',
      defaultDeleteButtonText: 'Continue'
    },
    info: {
      icon: 'ri-information-line',
      iconColor: 'text-info',
      confirmButtonColor: 'btn-primary',
      defaultTitle: 'Confirm',
      defaultDeleteButtonText: 'Confirm'
    }
  };

  const config = variantConfig[variant];

  // Generate dynamic message if not provided
  const defaultMessage = recordName 
    ? `Are you sure you want to remove this ${recordName}${recordId ? ` (${recordId})` : ''}?`
    : `Are you sure you want to remove this record${recordId ? ` ${recordId}` : ''}?`;

  const displayMessage = message || defaultMessage;
  const displayTitle = title || config.defaultTitle;
  const displayDeleteButtonText = deleteButtonText || config.defaultDeleteButtonText;

  return (
    <Modal fade={true} isOpen={show} toggle={onCloseClick} centered={true}>
      <ModalBody className="py-3 px-5">
        <div className="mt-2 text-center">
          <i className={`${config.icon} display-5 ${config.iconColor}`}></i>
          <div className="mt-4 pt-2 fs-15 mx-4 mx-sm-5">
            <h4>{displayTitle}</h4>
            <p className="text-muted mx-4 mb-0">
              {displayMessage}
            </p>
          </div>
        </div>
        <div className="d-flex gap-2 justify-content-center mt-4 mb-2">
          <button
            type="button"
            className="btn w-sm btn-light"
            data-bs-dismiss="modal"
            onClick={onCloseClick}
            disabled={isLoading}
          >
            {cancelButtonText}
          </button>
          <button
            type="button"
            className={`btn w-sm ${config.confirmButtonColor}`}
            id="delete-record"
            onClick={onDeleteClick}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Spinner size="sm" className="me-2" />
                {variant === 'delete' ? 'Deleting...' : 'Processing...'}
              </>
            ) : (
              displayDeleteButtonText
            )}
          </button>
        </div>
      </ModalBody>
    </Modal>
  ) as unknown as JSX.Element;
};

// Export type for external use
export type { ConfirmationVariant };

// Backward compatibility alias
export const ConfirmationModal = DeleteModal;

export default DeleteModal;